import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { streamPost } from '../sse.js';
import { answerAsk } from '../desk-model.js';
import { AGENTS } from '../agents.js';
import AgentMark from './AgentMark.jsx';
import A2aGroup from './A2aGroup.jsx';
import AgentTurn from './AgentTurn.jsx';

// Walk turns in order. Producer + agent turns pass through as `main`.
// Contiguous a2a turns get bundled into groups keyed by (date, from, to).
function buildFeed(turns) {
  const out = [];
  let i = 0;
  while (i < turns.length) {
    const t = turns[i];
    if (t.kind !== 'a2a') {
      out.push({ kind: 'main', turn: t, key: t._id || `m-${i}` });
      i++;
      continue;
    }
    const window = [];
    while (i < turns.length && turns[i].kind === 'a2a') { window.push(turns[i]); i++; }
    const groups = new Map();
    for (const a of window) {
      const k = `${a.date}|${a.from}|${a.to}`;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(a);
    }
    for (const [k, items] of groups) {
      const [date, from, to] = k.split('|');
      out.push({ kind: 'a2a-group', date, from, to, items, key: `g-${k}-${items[0]?._id || i}` });
    }
  }
  return out;
}

// ThreadDrawer — the thread (the steering wheel) demoted into a slide-over.
// Asks raised in-drawer land on the Desk automatically (backend sets `awaiting`).
export default function ThreadDrawer({ projectId, threadId, onClose, onChanged }) {
  const [thread, setThread] = useState(null);
  const [project, setProject] = useState(null);
  const [draft, setDraft] = useState('');
  const [live, setLive] = useState(null); // streaming agent text, or null
  const [busy, setBusy] = useState(false);
  const feedRef = useRef(null);
  const taRef = useRef(null);
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  // ESC closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let ok = true;
    setThread(null);
    api(`/api/threads/${threadId}`)
      .then(async t => {
        if (!ok || !alive.current) return;
        setThread(t);
        const p = await api(`/api/projects/${t.projectId || projectId}`);
        if (ok && alive.current) setProject(p);
      })
      .catch(() => { if (ok && alive.current) setThread(false); });
    return () => { ok = false; };
  }, [threadId, projectId]);

  // Keep the feed pinned to the bottom while streaming.
  useEffect(() => {
    if (live !== null && feedRef.current) {
      requestAnimationFrame(() => { feedRef.current.scrollTop = feedRef.current.scrollHeight; });
    }
  }, [live]);

  if (thread === false) {
    return (
      <>
        <div className="drawer-scrim" role="presentation" tabIndex={-1} onClick={onClose} />
        <aside className="drawer">
          <div className="dr-head">
            <span className="dr-title">Thread not found</span>
            <button type="button" className="dr-close" onClick={onClose}>ESC ×</button>
          </div>
        </aside>
      </>
    );
  }
  if (!thread || !project) {
    return (
      <>
        <div className="drawer-scrim" role="presentation" tabIndex={-1} onClick={onClose} />
        <aside className="drawer" />
      </>
    );
  }

  const a = AGENTS[thread.agent];
  const pid = project._id || projectId;
  const feed = buildFeed(thread.turns || []);
  // Most-recent open ask — drives the ask-pin in the head.
  const awaitingTurn = (thread.turns || []).findLast(t => t.kind === 'agent' && t.awaiting && t.ask);

  function appendTurn(t) {
    setThread(prev => ({ ...prev, turns: [...prev.turns, t] }));
  }

  async function handleA2aDismiss(item) {
    await api(`/api/projects/${pid}/a2a/dismiss`, {
      method: 'POST',
      body: { date: item.date, from: item.from, to: item.to, body: item.body },
    });
    setThread(prev => ({
      ...prev,
      turns: prev.turns.map(t =>
        t.kind === 'a2a' && t.date === item.date && t.from === item.from && t.to === item.to && t.body === item.body
          ? { ...t, dismissed: true }
          : t
      ),
    }));
  }

  async function handleA2aReply(item, replyBody) {
    const r = await api(`/api/projects/${pid}/a2a/reply`, {
      method: 'POST',
      body: { date: item.date, from: item.from, to: item.to, body: item.body, reply: replyBody },
    });
    setThread(prev => ({
      ...prev,
      turns: prev.turns.map(t =>
        t.kind === 'a2a' && t.date === item.date && t.from === item.from && t.to === item.to && t.body === item.body
          ? { ...t, dismissed: true, reply: r.reply }
          : t
      ),
    }));
  }

  function focusComposer() {
    taRef.current?.focus();
  }

  // refreshThread — reload the thread after an answer so the ask flips settled.
  async function refreshThread() {
    try {
      const t = await api(`/api/threads/${threadId}`);
      if (alive.current) setThread(t);
    } catch { /* leave the optimistic view in place */ }
    if (onChanged) onChanged();
  }

  // answerFromTurn — an option button on an in-feed ask answers it directly.
  function answerFromTurn(turnId, payload) {
    answerAsk(pid, turnId, payload, (evt) => {
      if (evt && (evt.type === 'done' || evt.type === 'error')) refreshThread();
    }).catch(() => refreshThread());
  }

  async function send() {
    const message = draft.trim();
    if (!message || busy) return;
    setDraft('');
    setBusy(true);
    // Optimistic you-turn.
    appendTurn({ kind: 'you', date: new Date().toISOString().slice(0, 10), body: message });
    let acc = '';
    setLive('');
    try {
      await streamPost(
        `/api/projects/${pid}/threads/${threadId}/turn`,
        { body: message },
        (evt) => {
          if (!alive.current) return;   // drawer closed mid-stream — drop late events
          if (evt.type === 'delta') { acc += evt.text; setLive(acc); }
          if (evt.type === 'done') {
            appendTurn(evt.turn);
            setLive(null);
            if (evt.handoffs && evt.handoffs.length) {
              evt.handoffs.forEach(h =>
                appendTurn({
                  kind: 'a2a',
                  date: new Date().toISOString().slice(0, 10),
                  from: thread.agent,
                  to: h.to,
                  body: h.text,
                })
              );
            }
            if (onChanged) onChanged();
          }
          if (evt.type === 'error') {
            setLive(null);
            alert(evt.message || 'Agent failed.');
          }
        }
      );
    } catch (err) {
      if (alive.current) { setLive(null); alert(err.message); }
    } finally {
      if (alive.current) { setBusy(false); taRef.current?.focus(); }
    }
  }

  return (
    <>
      <div className="drawer-scrim" role="presentation" tabIndex={-1} onClick={onClose} />
      <aside className="drawer">
        <div className="dr-head">
          <AgentMark agent={a} size="xs" />
          <span className="dr-title">{thread.title}</span>
          <button type="button" className="dr-close" onClick={onClose}>ESC ×</button>
        </div>

        {awaitingTurn && awaitingTurn.ask && (
          <div className="dr-sub">
            <strong style={{ color: 'var(--accent-ink)' }}>LATEST ASK</strong>{' '}
            "{awaitingTurn.ask.text}"
          </div>
        )}

        <div className="dr-feed" ref={feedRef}>
          {feed.map(node => {
            if (node.kind === 'main') {
              if (node.turn.kind === 'agent') {
                return (
                  <AgentTurn
                    key={node.key}
                    turn={node.turn}
                    threadAgent={a}
                    projectId={pid}
                    onNav={(to) => { window.location.href = to; }}
                    focusComposer={focusComposer}
                    onAnswer={(payload) => answerFromTurn(node.turn._id, payload)}
                  />
                );
              }
              return <YouTurn key={node.key} turn={node.turn} />;
            }
            return (
              <A2aGroup
                key={node.key}
                from={node.from}
                to={node.to}
                date={node.date}
                items={node.items}
                onDismiss={handleA2aDismiss}
                onReply={handleA2aReply}
                scope="thread"
              />
            );
          })}

          {live !== null && (
            <article className="turn-agent is-awaiting">
              <div className="meta">
                <span className="agent-info">
                  <AgentMark agent={a} size="xs" />
                  <span className="name">{a?.name?.toUpperCase()}</span>
                </span>
                <span className="sep">·</span>
                <span>NOW</span>
              </div>
              <div className="body">
                {live
                  ? live.split('\n').map((p, i) => <p key={i}>{p}</p>)
                  : <p style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>Thinking…</p>}
              </div>
            </article>
          )}
        </div>

        <div className="dr-composer">
          <textarea
            ref={taRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={`Push back, redirect, or add context for ${a?.name?.toLowerCase() || 'the agent'}…`}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
          />
          <div className="drc-foot">
            <span className="drc-note">⌘↩ to send · asks raised here land on the desk</span>
            <button
              type="button"
              className="btn accent"
              style={{ marginLeft: 'auto' }}
              disabled={!draft.trim() || busy}
              onClick={send}
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// You-turn only; agent turns are handled by <AgentTurn>.
function YouTurn({ turn }) {
  return (
    <div className="dr-turn you">
      <div className="t-meta">
        <span className="mono-label" style={{ color: 'var(--ink-2)' }}>YOU</span>
        <span className="t-when">{turn.date?.toUpperCase()}</span>
      </div>
      <div className="t-body"><p>"{turn.body}"</p></div>
    </div>
  );
}
